<template>
    <div ref="verificationForm" class="row mt-5">
	    <div v-if="!recoveryMode">
	        <div class="col-sm-12 mb-3" v-if="header">
	            <div v-if="twoFactorType === 'email'" class="d-flex flex-column gap-3 text-center mb-5">
	                <h3 class="fw-semi-bold m-0">Verification Code</h3>
		            <p class="text-secondary m-0">A verification code has been sent via email to <strong>{{ mfa_email }}</strong></p>
		            <a href="javascript:void(0)" class="cursor-pointer small" v-if="allowSwitching" @click="switchTwoFactorMethod">Switch to Authenticator App 2FA</a>
		            <div>
			            <button v-if="resendTimer <= 0" class="btn btn-link p-0" :disabled="resendTimer > 0" @click="resendEmail" > Resend Email </button>
			            <small v-if="resendTimer > 0" class="text-secondary"> You can resend the email in <strong>{{ formattedResendTimer }}</strong> </small>
		            </div>
	            </div>
		        <div v-if="twoFactorType === 'two_factor'" class="d-flex flex-column gap-3 text-center mb-5">
			        <h3 class="fw-semi-bold m-0">Authentication Code</h3>
			        <p class="text-secondary m-0">Please enter a code from your Authenticator App</p>
			        <a href="javascript:void(0)" class="cursor-pointer small" v-if="allowSwitching" @click="switchTwoFactorMethod">Switch to Email Based 2FA</a>
		        </div>
		        <div v-if="formErrors.code" class="validation-error text-center" v-html="formErrors.code"></div>
	        </div>

	        <div class="col-12 d-flex align-items-center justify-content-center flex-wrap gap-2">
	            <input v-for="(key, index) in Object.keys(otpCode)" :id="'code_' + index" :key="'code_' + index" v-model="otpCode[key]"
	                :tabindex="index + 1"
	                :autofocus="index === emptyOptCode || (index === (Object.keys(otpCode).length - 1) && emptyOptCode === -1)"
	                @input="handleFocusAndCharacterEntered" @keydown="handleKeydown" @paste="handlePaste"
	                class="verification-code-input active-border form-control" type="text" inputmode="numeric" maxlength="1"
	                pattern="\d{1}">
	        </div>

		    <div v-if="autoSubmit && formErrors.code" class="col-12 mt-5">
			    <div class="validation-error text-center" v-html="formErrors.code"></div>
		    </div>

		    <div v-if="!autoSubmit" class="col-sm-8 offset-sm-2 col-md-6 offset-md-3 col-xl-4 offset-xl-4 d-flex gap-3 align-items-center mt-4">
			    <button class="btn btn-outline-secondary rounded-pill py-7 w-50" @click="closeCodeVerification">Cancel</button>
			    <button class="btn btn-dark rounded-pill py-7 w-50" @click="verifyCode" :disabled="!isOtpCodeFilled">Submit</button>
		    </div>

	        <div v-if="allow_recovery_code" class="col-sm-8 offset-sm-2 col-md-6 offset-md-3 col-xl-4 offset-xl-4 mb-3">
	            <div class="d-flex flex-column gap-3 text-center mb-5">
	                <p class="text-secondary m-0 mt-5">
	                    Lost access? Use your <a class="text-dark cursor-pointer" href="javascript:void(0)" @click="recoveryMode = !recoveryMode">Recovery Code</a>
	                </p>
	            </div>
	        </div>
	    </div>

        <div v-if="recoveryMode">
	        <div class="col-sm-8 offset-sm-2 col-md-6 offset-md-3 col-xl-4 offset-xl-4 mb-3">
		        <div class="d-flex flex-column gap-3 text-center mb-5">
					<h3 class="fw-semi-bold m-0">Recovery Code</h3>
					<p class="text-secondary m-0">Enter your recovery code to verify your account. Recovery Codes are case sensitive.</p>
			        <a class="text-dark cursor-pointer" href="javascript:void(0)" @click="recoveryMode = !recoveryMode">Back to Verification Code</a>
				</div>
				<div v-if="formErrors.code" class="validation-error text-center" v-html="formErrors.code"></div>
	        </div>
			<div class="col-12 d-flex align-items-center justify-content-center flex-wrap gap-1">
				<input v-for="(key, index) in Object.keys(otpCode)" :id="'recovery_code_' + index" v-model="otpCode[key]"
					:tabindex="index + 1"
					:autofocus="index === emptyOptCode || (index === (Object.keys(otpCode).length - 1) && emptyOptCode === -1)"
					@input="handleFocusAndCharacterEntered" @keydown="handleKeydown" @paste="handlePaste"
					class="verification-code-input active-border form-control" type="text" maxlength="1" pattern="[A-Za-z0-9]{1}">
			</div>
			<div class="col-sm-8 offset-sm-2 col-md-6 offset-md-3 col-xl-4 offset-xl-4 d-flex gap-3 align-items-center mt-4">
				<button class="btn btn-outline-secondary rounded-pill py-7 w-50" @click="closeCodeVerification">Cancel</button>
				<button class="btn btn-dark rounded-pill py-7 w-50" @click="verifyCode('recovery')" :disabled="!isOtpCodeFilled">Submit</button>
			</div>
        </div>

    </div>

</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import apiClient from "@/utils/axios.js";
import { useHttpErrorManager } from "@/composables/useHttpErrorManager.js";
import { useToast } from "@/composables/useToast.js";
import { useAppHelpers } from "@/composables/useAppHelpers.js";

const props = defineProps({
	type: { type: String, required: true, default: 'two_factor' },
	mfa_email: { type: [String, null], required: false, default: null },
	user_id: { type: [String, Number, null], required: false, default: null },
	uid: { type: [String, Number, null], required: false, default: null },
	allow_recovery_code: { type: Boolean, required: false, default: false },
	purpose: { type: String, required: false, default: 'email_verification' },
	timer : { type: Number, required: false, default: 300 },
	from : { type: String, required: false, default: null },
	header: { type: Boolean, required: false, default: true },
	autoSubmit: { type: Boolean, required: false, default: false },
	allowSwitching: { type: Boolean, required: false, default: false }
});

const emits = defineEmits(['confirmationStatus', 'closeVerification']);
const { displayToast } = useToast();
const helper = useAppHelpers();
const formErrors = ref({});

const twoFactorType = ref(props.type);
watch(() => props.type, (value) => {
	twoFactorType.value = value;
});

const switchTwoFactorMethod = () => {
	if(twoFactorType.value === 'email'){
		twoFactorType.value = 'two_factor';
	}
	else{
		twoFactorType.value = 'email';
		resendEmail();
	}
}

const verificationForm = ref(null);
const recoveryMode = ref(false);
const otpCode = ref({ 'first': '', 'second': '', 'third': '', 'fourth': '', 'fifth': '', 'sixth': '' });

// watch recoveryMode
watch(recoveryMode, (value) => {
	if(value){
		mode.value = 'recovery';
		otpCode.value = { 'first': '', 'second': '', 'third': '', 'fourth': '', 'fifth': '', 'sixth': '', 'seventh': '', 'eighth': '' };
	}
	else {
		mode.value = 'normal';
		otpCode.value = {'first': '', 'second': '', 'third': '', 'fourth': '', 'fifth': '', 'sixth': ''};
	}
});

const emptyOptCode = computed(() => {
    return Object.keys(otpCode.value).findIndex(key => !otpCode.value[key]);
});

const isOtpCodeFilled = computed(() => {
    return emptyOptCode.value === -1;
});

watch(() => isOtpCodeFilled.value, (value) => {
	if (value && props.autoSubmit) {
		verifyCode();
	}
});

const handleFocusAndCharacterEntered = (event) => {
	const emptyIndex = emptyOptCode.value;
	if (emptyIndex > -1 && emptyIndex <= Object.keys(otpCode.value).length - 1) {
		document.querySelectorAll('.verification-code-input')[emptyIndex].focus();
	}
	else{
		document.querySelectorAll('.verification-code-input')[Object.keys(otpCode.value).length - 1].focus();
	}
};

const handleKeydown = (event) => {
	const inputs = document.querySelectorAll('.verification-code-input');
	const currentIndex = parseInt(event.target.tabIndex) - 1;
	const currentKey = Object.keys(otpCode.value)[currentIndex];

	if (event.ctrlKey && event.key.toLowerCase() === 'v') {
		return;
	}

	if (event.key === 'Backspace') {
		otpCode.value[Object.keys(otpCode.value)[currentIndex]] = '';

		if (currentIndex > 0) {
			nextTick(() => {
				inputs[currentIndex - 1]?.focus();
			});
		}
		event.preventDefault();
	}

	if(event.key === 'Enter'){
		if(isOtpCodeFilled.value){
			verifyCode();
		}
	}

	else if (event.key === 'Delete') {
		otpCode.value[Object.keys(otpCode.value)[currentIndex]] = '';
		event.preventDefault();
	}
	else if(/^[a-zA-Z0-9]$/.test(event.key)){
		event.preventDefault();
		const cursorPosition = event.target.selectionStart;
		let cursorStart = true;
		if (cursorPosition === 0) {
			otpCode.value[currentKey] = event.key;
		} else if (cursorPosition === 1) {
			cursorStart = false;
			if (currentIndex < inputs.length - 1) {
				otpCode.value[Object.keys(otpCode.value)[currentIndex + 1]] = event.key;
			}
		}
		if (currentIndex < inputs.length - 1) {
			nextTick(() => {
				const nextInput = inputs[currentIndex + 1];
				if (nextInput) {
					nextInput.focus();
					if(cursorStart){
						nextInput.setSelectionRange(0, 0);
					}
				}
			});
		}
	}
};

const handlePaste = (event) => {
	event.preventDefault();
	const pastedData = event.clipboardData.getData('text').trim();
	if (pastedData) {
		const inputs = document.querySelectorAll('.verification-code-input');
		const currentIndex = parseInt(event.target.tabIndex) - 1; // Start from the current field
		const otpKeys = Object.keys(otpCode.value);

		pastedData.split('').forEach((char, i) => {
			const targetIndex = currentIndex + i;
			if (targetIndex < otpKeys.length) {
				otpCode.value[otpKeys[targetIndex]] = char;
			}
		});

		nextTick(() => handleFocusAndCharacterEntered());
	}
};

const mode = ref('normal');
const verifyCode = async (verifyMode='normal') => {
	try {
		mode.value = verifyMode;
		helper.loaderOn(verificationForm.value);
		if (!isOtpCodeFilled.value) {
			displayToast('Please enter the complete verification code', 'outline-danger', 8000);
			helper.loaderOff(verificationForm.value);
			return;
		}
		const code = Object.values(otpCode.value).join('');
		let url = '/auth/verifyCode';

		let purpose = props.purpose;
		if(twoFactorType.value === 'email' && purpose === 'two_factor') {
			purpose = 'two_factor_email';
		}

		let data = { code: code,user_id: props.user_id, email: props.mfa_email, purpose: purpose };

		if(mode.value === 'recovery') {
			data.purpose = 'recovery_code';
		}

		if(props.from){
			data['from'] = props.from;
		}

		if (props.uid) {
			data['uid'] = props.uid;
			data['access'] = 'restricted';
		}

		const response = await apiClient.post(url, data);

		if(mode.value === 'recovery' && response.data.message){
			displayToast(response.data.message, 'outline-success', 8000);
		}

		helper.loaderOff(verificationForm.value);
		if (response.status >= 200 && response.status < 300) {
			emits('confirmationStatus', [true, twoFactorType.value, mode.value]);
		}
	} catch (error) {
		let errorObj = await useHttpErrorManager().handleError(error);
		let message = errorObj['full_message'];
		if (error.response.status === 422) {
			formErrors.value = errorObj;
			if(errorObj['code'] && errorObj['code'].includes('A new verification code has been sent to your email address')){
				resendTimer.value = 300;
			}
		}
		else{
			displayToast(message, 'outline-danger', 8000);
		}
		helper.loaderOff(verificationForm.value);
	}
}

const closeCodeVerification = () => {
	emits('confirmationStatus', [false, twoFactorType.value, mode.value]);
	emits('closeVerification');
}

const resendTimer = ref(props.timer ? props.timer : 300);
const timerInterval = ref(null);

const resendEmail = async () => {
	try {
		let tempPurpose = props.purpose;
		if(twoFactorType.value === 'email' && tempPurpose === 'two_factor') {
			tempPurpose = 'two_factor_email';
		}
		if(tempPurpose !== 'email_verification' && tempPurpose !== 'two_factor_email'){
			displayToast('Invalid Resend Email Attempt', 'outline-danger', 5000);
			return;
		}

		helper.loaderOn(verificationForm.value);
		const response = await apiClient.post('/auth/resendVerificationCodeEmail', {
			email: props.mfa_email,
			user_id: props.user_id,
			purpose: tempPurpose
		});

		if (response.status >= 200 && response.status < 300) {
			displayToast(response.data.message, 'outline-success', 8000);
			resendTimer.value = response.data.expire_seconds;
			startTimer();
		}
		helper.loaderOff(verificationForm.value);
	} catch (error) {
		let errorObj = await useHttpErrorManager().handleError(error);
		let message = errorObj['full_message'];
		displayToast(message, 'outline-danger', 8000);
		helper.loaderOff(verificationForm.value);
	}
};

const startTimer = () => {
	clearInterval(timerInterval.value);
	timerInterval.value = setInterval(() => {
		if (resendTimer.value > 0) {
			resendTimer.value--;
		} else {
			clearInterval(timerInterval.value);
		}
	}, 1000);
};

const formattedResendTimer = computed(() => {
	const minutes = Math.floor(resendTimer.value / 60);
	const seconds = Math.floor(resendTimer.value % 60);
	return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
});

onMounted(() => {
	nextTick(() => handleFocusAndCharacterEntered());
	if(props.timer){
		startTimer();
	}
	if(twoFactorType.value === 'email'){
		resendEmail();
	}
});

onUnmounted(() => {
	if(timerInterval.value){
		clearInterval(timerInterval.value);
	}
});

</script>